'use client'

import React from 'react'
import { ChevronDown, Minus, Plus } from 'lucide-react'
import { BagCheck, TrashBin2 } from 'solar-icons'
import { toast } from 'sonner'
import { useCart, useModals } from '@/lib/hooks'
import { handleNumberInput } from '@/lib/utils'

import {
  Badge,
  Barcode,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Separator,
  Thumbnail,
} from '@/components/ui'

interface EditCartItemProps extends React.ComponentPropsWithoutRef<'form'> {
  product: Product
}

export const EditCartItem = ({ product }: EditCartItemProps) => {
  const { updateCartProduct, removeProduct } = useCart() as any
  const { closeModal } = useModals()
  const cartId = product?.cart_id
  const productId = product?.id
  const hasWarranty =
    product?.has_warranty === 1 || product?.has_warranty === true

  const [qty, setQty] = React.useState<number>(product?.quantity ?? 1)
  const [discountType, setDiscountType] = React.useState<string>('fixed')
  const [discount, setDiscount] = React.useState<number>(
    product?.discount_per_qty === 0 ? undefined : product?.discount_per_qty,
  )

  const handleSubmit = async (data: FormData) => {
    const qty = Number(data.get('qty'))

    if (!cartId || !productId) {
      return toast.error('Cart ID or Product ID is missing.')
    }

    if ((Number.isNaN(qty) || qty < 1) && !hasWarranty) {
      return toast.error('Quantity must be a number greater than 0.')
    }

    try {
      await updateCartProduct({
        cartId,
        productId,
        quantity: !hasWarranty ? qty : product?.quantity,
        discount,
        discountType,
        qtyChange: !hasWarranty
          ? Number(product?.quantity) !== Number(qty)
          : false,
      })
      toast.success('Cart item updated successfully.')
      closeModal('EDIT_CART_ITEM')
    } catch (error) {
      toast.error('Failed to update cart item.')
    }
  }

  const handleRemoveProduct = () => {
    removeProduct({ productId, cartId })
    closeModal('EDIT_CART_ITEM')
  }

  const fallbackTitle = `${product?.product_name} ${product?.varient_options}`

  return (
    <form
      className="flex w-full flex-wrap gap-4 lg:gap-8"
      action={handleSubmit}
    >
      <Thumbnail size="lg" src={product?.image_url} />

      <div className="flex w-full max-w-[396px] flex-col flex-wrap gap-4 pr-4">
        <h2 className="text-balance text-xl font-medium">
          {product?.variant_label ?? fallbackTitle}
        </h2>

        <div className="flex w-full flex-col gap-4">
          <div className="flex w-full flex-col gap-4 md:flex-row">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="w-[36px] px-0" size="md">
                SKU
              </Badge>
              <span className="font-mono text-sm font-medium text-gray-950">
                {product?.sku}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="min-w-5 px-1" size="md">
                <Barcode className="h-3" />
              </Badge>
              <span className="font-mono text-sm font-medium text-gray-950">
                {product?.barcode}
              </span>
            </div>
          </div>
        </div>

        <div className="flex w-full flex-col gap-5">
          {!hasWarranty && (
            <div className="flex w-full flex-col gap-2">
              <div className="flex w-full items-center gap-2">
                <Label className="shrink-0 !text-sm font-medium text-gray-700">
                  Quantity
                </Label>
                <Separator className="mt-1" />
              </div>

              <div className="flex items-center gap-2">
                <Input
                  className="max-w-[200px] appearance-none text-center"
                  value={qty}
                  inputSize="lg"
                  min={1}
                  name="qty"
                  onChangeValue={(value) => {
                    setQty(Number(value))
                  }}
                  onKeyDown={handleNumberInput}
                  type="number"
                  autoFocus
                />
                <div className="flex w-full items-center gap-2">
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      if (qty > 1) {
                        setQty((_prevQty) => _prevQty - 1)
                      }
                    }}
                  >
                    <Minus className="size-5" strokeWidth={1.5} />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full"
                    onClick={() => setQty((_prevQty) => _prevQty + 1)}
                  >
                    <Plus className="size-5" strokeWidth={1.5} />
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="flex w-full flex-col gap-2">
            <div className="flex w-full items-center gap-2">
              <Label className="shrink-0 !text-sm font-medium text-gray-700">
                {hasWarranty ? 'Discount' : 'Discount per Item'}
              </Label>
              <Separator className="mt-1" />
            </div>

            <div className="flex items-center gap-2">
              <Input
                className="max-w-[200px] appearance-none text-center"
                value={discount?.toString()}
                inputSize="lg"
                name="discount"
                onChangeValue={(value) => setDiscount(Number(value))}
                type="number"
                placeholder="0"
              />
              <div className="flex w-full items-center gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="lg"
                      variant="outline"
                      className="w-full justify-between"
                    >
                      <span>
                        {discountType === 'fixed'
                          ? '(Rs) Fixed Amount'
                          : '(%) Percentage'}
                      </span>
                      <ChevronDown className="size-5" strokeWidth={1.5} />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-[var(--radix-dropdown-menu-trigger-width)] p-0">
                    <div className="flex w-full flex-col gap-0.5 bg-gray-50 p-1">
                      <DropdownMenuItem className="w-full" asChild>
                        <Button
                          variant="outline"
                          className="w-full rounded-sm shadow-none"
                          onClick={() => setDiscountType('fixed')}
                        >
                          (Rs) Fixed Amount
                        </Button>
                      </DropdownMenuItem>
                      <DropdownMenuItem className="w-full" asChild>
                        <Button
                          variant="outline"
                          className="w-full rounded-sm shadow-none"
                          onClick={() => setDiscountType('percentage')}
                        >
                          (%) Percentage
                        </Button>
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </div>
        </div>

        <div className="flex w-full gap-2 pt-3">
          <Button
            variant="brand"
            className="w-full gap-3 shadow-card"
            size="lg"
            type="submit"
          >
            <BagCheck className="size-5" strokeWidth={1.5} />
            <span>Update Cart</span>
          </Button>

          <Button
            variant="destructive"
            className="w-[100px] gap-3 shadow-card"
            size="lg"
            type="button"
            onClick={handleRemoveProduct}
          >
            <TrashBin2 className="size-5" strokeWidth={1.5} />
          </Button>
        </div>
      </div>
    </form>
  )
}
